import { useEffect, useState } from "react";
import { FileText, Paperclip, CircleDot, CheckCircle2, Clock, Hourglass, UserCheck } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime, rp } from "@/lib/format";

export const STATUS_STYLE = {
  baru: "bg-amber-100 text-amber-800",
  diproses: "bg-blue-100 text-blue-800",
  menunggu_anggota: "bg-violet-100 text-violet-800",
  selesai: "bg-emerald-100 text-emerald-800",
};
const STATUS_ICON = { baru: Clock, diproses: CircleDot, menunggu_anggota: Hourglass, selesai: CheckCircle2 };
export const KIND = { angsuran: "Angsuran pinjaman", setoran: "Setoran simpanan", biaya_admin: "Biaya administrasi", transfer_bank: "Transfer bank", transfer_member: "Transfer antar anggota" };
export const REQ_STATUS = { pending: "Menunggu verifikasi", menunggu: "Menunggu verifikasi", disetujui: "Disetujui", ditolak: "Ditolak" };

export const StatusBadge = ({ status, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[status]}`}>{label}</span>
);

const STEPS = [["baru", "Diterima"], ["diproses", "Diproses"], ["selesai", "Selesai"]];
export const StatusSteps = ({ status }) => {
  const idx = status === "selesai" ? 2 : status === "baru" ? 0 : 1;
  return (
    <div className="flex items-center gap-1" data-testid="ticket-steps">
      {STEPS.map(([k, l], i) => (
        <div key={k} className="flex-1 flex flex-col items-center gap-1">
          <div className={`h-1.5 w-full rounded-full ${i <= idx ? "bg-emerald-500" : "bg-slate-200"}`} />
          <span className={`text-[10px] font-semibold ${i <= idx ? "text-emerald-700" : "text-slate-400"}`}>{l}</span>
        </div>
      ))}
    </div>
  );
};

export const RelatedTx = ({ r }) => r ? (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="ticket-related">
    <div className="font-semibold text-slate-800">{KIND[r.kind] || r.kind} • {rp(r.amount)}</div>
    <div className="text-slate-500">{fmtDateTime(r.created_at)} • {REQ_STATUS[r.status] || r.status}{r.ref_no ? ` • ${r.ref_no}` : ""}</div>
  </div>
) : null;

const FileChip = ({ tid, f }) => {
  const [url, setUrl] = useState(null);
  const img = f.mime.startsWith("image/");
  useEffect(() => {
    let u;
    api.get(`/help/tickets/${tid}/files/${f.file_id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [tid, f.file_id]);
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" download={img ? undefined : f.name} data-testid={`ticket-file-${f.file_id}`}
      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-1.5 pr-3 max-w-[220px]">
      {img && url ? <img src={url} alt={f.name} className="h-10 w-10 rounded object-cover" /> : <span className="h-10 w-10 rounded bg-red-600 text-white flex items-center justify-center"><FileText className="h-5 w-5" /></span>}
      <span className="text-[11px] truncate">{f.name}</span>
    </a>
  );
};

const ROLE_STYLE = { member: "bg-white border-slate-200", staff: "bg-blue-50 border-blue-100", system: "bg-amber-50 border-amber-100" };

export const Timeline = ({ t, statuses }) => (
  <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3" data-testid="ticket-timeline">
    {t.timeline.map((e) => {
      const Icon = e.by_role === "system" ? UserCheck : e.status ? STATUS_ICON[e.status] : Paperclip;
      return (
        <li key={e.entry_id} className="ml-4" data-testid={`ticket-entry-${e.entry_id}`}>
          <span className="absolute -left-[9px] mt-1 h-4 w-4 rounded-full bg-white border-2 border-emerald-500 flex items-center justify-center"><Icon className="h-2 w-2 text-emerald-600" /></span>
          <div className={`rounded-xl border p-3 ${ROLE_STYLE[e.by_role]}`}>
            <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
              <b className="text-slate-800">{e.by_role === "member" ? `${e.by_name} (Anggota)` : e.by_role === "system" ? "Sistem" : `${e.by_name} (Petugas CU)`}</b>
              <span>{fmtDateTime(e.at)}</span>
            </div>
            {e.status && <div className="mt-1"><StatusBadge status={e.status} label={`Status: ${statuses[e.status]}`} /></div>}
            {e.text && <p className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{e.text}</p>}
            {e.file_ids?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">{t.files.filter((f) => e.file_ids.includes(f.file_id)).map((f) => <FileChip key={f.file_id} tid={t.ticket_id} f={f} />)}</div>
            )}
          </div>
        </li>
      );
    })}
  </ol>
);

export const ReplyBox = ({ onSend, placeholder, testid = "ticket-reply" }) => {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await onSend(text, files);
      setText("");
      setFiles([]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2" data-testid={testid}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={placeholder} data-testid={`${testid}-text`}
        className="w-full text-sm rounded-xl border border-slate-200 px-3 py-2 outline-none focus:ring-2 focus:ring-emerald-500/30 bg-white" />
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-slate-50">
          <Paperclip className="h-4 w-4" /> Lampiran {files.length ? `(${files.length})` : ""}
          <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid={`${testid}-file`}
            onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 3))} />
        </label>
        <button onClick={send} disabled={busy || (!text.trim() && !files.length)} data-testid={`${testid}-send`}
          className="ml-auto bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg">{busy ? "Mengirim..." : "Kirim"}</button>
      </div>
    </div>
  );
};

export const toForm = (fields, files) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => v != null && v !== "" && fd.append(k, v));
  (files || []).forEach((f) => fd.append("files", f, f.name));
  return fd;
};
